import {
  ALL_PRICE_BUCKETS,
  DEFAULT_SCHEDULE_SELECTION,
  DEFAULT_STATUS_SELECTION,
  PRICE_BUCKET_RANGES_CENTS
} from './OfferFilters.consts';
import {
  IOffersFilterBackendPayload,
  IOffersFilterState,
  OfferPriceBucket,
  OfferScheduleType,
  OfferStatusFilterValue
} from './OfferFilters.types';

export const narrowToEnum = <T extends string>(
  values: string[],
  enumObj: Record<string, T>
): T[] => {
  const allowed = new Set<string>(Object.values(enumObj));
  return values.filter((v: string): v is T => allowed.has(v));
};

export const toggleArrayValue = <T>(current: T[], value: T): T[] =>
  current.includes(value)
    ? current.filter((v: T): boolean => v !== value)
    : [...current, value];

export const buildOffersBackendPayload = (
  filters: IOffersFilterState
): IOffersFilterBackendPayload => {
  const payload: IOffersFilterBackendPayload = {};

  const searchPayload = buildSearchPayload(filters.search);
  Object.assign(payload, searchPayload);

  const statusPayload = buildStatusPayload(filters.statuses);
  Object.assign(payload, statusPayload);

  const schedulePayload = buildSchedulePayload(filters.schedules);
  Object.assign(payload, schedulePayload);

  const offerDesignPayload = buildOfferDesignPayload(filters.offerDesignIds);
  Object.assign(payload, offerDesignPayload);

  if (filters.createdAtFrom) {
    payload.createdAtFrom = filters.createdAtFrom;
  }
  if (filters.createdAtUntil) {
    payload.createdAtUntil = filters.createdAtUntil;
  }

  const pricePayload = buildPricePayload(filters.priceBuckets);
  Object.assign(payload, pricePayload);

  return payload;
};

const buildSearchPayload = (
  search: string
): Pick<IOffersFilterBackendPayload, 'filterText'> => {
  const trimmed = search.trim();
  return trimmed ? { filterText: trimmed } : {};
};

// Active+Inactive (or none) → omit. Single value → active=true|false.
const buildStatusPayload = (
  selection: OfferStatusFilterValue[]
): Pick<IOffersFilterBackendPayload, 'active'> => {
  if (
    selection.length === 0 ||
    selection.length === DEFAULT_STATUS_SELECTION.length
  ) {
    return {};
  }
  return { active: selection[0] === OfferStatusFilterValue.ACTIVE };
};

// Permanent+Interval (or none) → omit. Otherwise send the picked subset.
const buildSchedulePayload = (
  selection: OfferScheduleType[]
): Pick<IOffersFilterBackendPayload, 'schedules'> => {
  if (
    selection.length === 0 ||
    selection.length === DEFAULT_SCHEDULE_SELECTION.length
  ) {
    return {};
  }
  return { schedules: selection };
};

const buildOfferDesignPayload = (
  ids: string[]
): Pick<IOffersFilterBackendPayload, 'offerDesignIds'> => {
  return ids.length === 0 ? {} : { offerDesignIds: ids };
};

const buildPricePayload = (
  buckets: OfferPriceBucket[]
): Pick<IOffersFilterBackendPayload, 'priceMin' | 'priceMax'> => {
  if (buckets.length === 0 || buckets.length === ALL_PRICE_BUCKETS.length) {
    return {};
  }

  const ranges = buckets.map((b: OfferPriceBucket) => PRICE_BUCKET_RANGES_CENTS[b]);
  const min = Math.min(...ranges.map((r: { min: number }) => r.min));
  const maxes = ranges.map((r: { max: number | null }) => r.max);
  const hasOpenEnded = maxes.some((m: number | null) => m === null);
  const payload: { priceMin?: number; priceMax?: number } = { priceMin: min };
  if (!hasOpenEnded) {
    payload.priceMax = Math.max(...(maxes as number[]));
  }
  return payload;
};

export const emptyOffersFilterState = (): IOffersFilterState => ({
  search: '',
  statuses: [...DEFAULT_STATUS_SELECTION],
  schedules: [...DEFAULT_SCHEDULE_SELECTION],
  offerDesignIds: [],
  createdAtFrom: '',
  createdAtUntil: '',
  priceBuckets: []
});

export const hasAnyActiveFilter = (filters: IOffersFilterState): boolean => {
  return (
    filters.search.trim() !== '' ||
    filters.offerDesignIds.length > 0 ||
    filters.createdAtFrom !== '' ||
    filters.createdAtUntil !== '' ||
    filters.priceBuckets.length > 0 ||
    filters.statuses.length !== DEFAULT_STATUS_SELECTION.length ||
    filters.schedules.length !== DEFAULT_SCHEDULE_SELECTION.length
  );
};
